import { ChevronRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useCurrency } from "@/hooks/useUserCurrency";

interface Loan {
  id: string;
  amount: number;
  dueDate: Date;
  status: "paid" | "active" | "overdue";
  loanDate: Date;
}

interface Payment { amount: number; paidAt: Date }

interface Props {
  loans: Loan[];
  payments: Payment[];
  onSeeReport?: () => void;
}

export function PortfolioHealthPanel({ loans, payments, onSeeReport }: Props) {
  const today = new Date();
  const { format } = useCurrency();
  const totalOpen = loans
    .filter((l) => l.status !== "paid")
    .reduce((s, l) => s + l.amount, 0);

  const open = loans.filter((l) => l.status !== "paid");
  const openCount = open.length || 1;

  const dayMs = 24 * 60 * 60 * 1000;
  const onTime = open.filter((l) => (l.dueDate.getTime() - today.getTime()) / dayMs > 7).length;
  const recent = open.filter((l) => {
    const ageDays = (today.getTime() - l.loanDate.getTime()) / dayMs;
    return ageDays <= 7 && (l.dueDate.getTime() - today.getTime()) / dayMs > 7;
  }).length;
  const upcoming = open.filter((l) => {
    const d = (l.dueDate.getTime() - today.getTime()) / dayMs;
    return d >= 0 && d <= 7;
  }).length;
  const overdue = open.filter((l) => l.dueDate.getTime() < today.getTime()).length;

  // Combine: green=onTime+recent (em dia), blue=recent share within em dia, amber=upcoming, red=overdue
  const segments = [
    { key: "Em dia", count: Math.max(onTime - recent, 0), color: "hsl(159 83% 38%)" },
    { key: "Recentes", count: recent, color: "hsl(225 75% 58%)" },
    { key: "Próx. vencer", count: upcoming, color: "hsl(28 92% 50%)" },
    { key: "Vencidos", count: overdue, color: "hsl(4 70% 59%)" },
  ];
  const total = segments.reduce((s, seg) => s + seg.count, 0) || 1;
  const healthyPct = Math.round(((segments[0].count + segments[1].count) / total) * 100);

  // Area chart: cumulative receivables over last 12 months
  const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  const monthsData: { month: string; valor: number }[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    monthsData.push({ month: monthNames[d.getMonth()], valor: 0 });
  }
  const sorted = [...payments].sort((a, b) => a.paidAt.getTime() - b.paidAt.getTime());
  let acc = 0;
  const buckets: Record<string, number> = {};
  sorted.forEach((p) => {
    acc += p.amount;
    const key = `${p.paidAt.getFullYear()}-${p.paidAt.getMonth()}`;
    buckets[key] = acc;
  });
  let last = 0;
  for (let i = 11; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (buckets[key] !== undefined) last = buckets[key];
    monthsData[11 - i].valor = last;
  }

  return (
    <div className="surface-card p-5 lg:p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-base lg:text-[17px] font-semibold tracking-tight text-foreground">Saúde da carteira</h3>
        <button
          onClick={onSeeReport}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-[hsl(225_75%_53%)] hover:text-[hsl(228_74%_45%)] transition-colors"
        >
          Ver relatório <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="text-[13px] text-muted-foreground mb-3">
        <span className="text-foreground font-semibold tabular">
          {format(totalOpen, { maximumFractionDigits: 0 })}
        </span>{" "}
        em risco distribuído{" "}
        <span className="text-[hsl(159_83%_34%)] font-semibold ml-1">{healthyPct}% saudável</span>
      </div>

      {/* Segmented bar */}
      <div className="flex gap-[3px] h-[10px] rounded-full overflow-hidden mb-3">
        {segments.map((seg) => {
          const pct = (seg.count / total) * 100;
          if (pct === 0) return null;
          return (
            <span
              key={seg.key}
              className="block h-full rounded-full"
              style={{ width: `${pct}%`, backgroundColor: seg.color }}
            />
          );
        })}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11.5px] text-muted-foreground mb-4">
        {segments.map((seg) => (
          <div key={seg.key} className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: seg.color }} />
            <span>
              {seg.key} · {Math.round((seg.count / total) * 100)}%
            </span>
          </div>
        ))}
      </div>

      {/* Area chart */}
      <div className="h-[200px] -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={monthsData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="healthArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(159 83% 40%)" stopOpacity={0.35} />
                <stop offset="100%" stopColor="hsl(159 83% 40%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(228 30% 92%)" strokeDasharray="3 5" vertical={false} />
            <XAxis
              dataKey="month"
              stroke="hsl(228 16% 55%)"
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis hide />
            <Tooltip
              cursor={{ stroke: "hsl(159 83% 40% / 0.4)", strokeWidth: 1 }}
              contentStyle={{
                background: "#fff",
                border: "1px solid hsl(228 30% 92%)",
                borderRadius: 10,
                fontSize: 12,
                boxShadow: "0 8px 24px -8px hsl(232 60% 13% / 0.16)",
              }}
              formatter={(v: number) => format(v)}
            />
            <Area
              type="monotone"
              dataKey="valor"
              stroke="hsl(159 83% 36%)"
              strokeWidth={2.5}
              fill="url(#healthArea)"
              dot={false}
              activeDot={{ r: 4, fill: "hsl(159 83% 34%)", stroke: "#fff", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}